import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Plus, MessageSquare, Mic, ArrowUpRight, Loader2, Trash2, Star, Copy, Check } from "lucide-react";

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const LANGUAGES = [
  { code: "auto", label: "Auto-detect (recommended)" },
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "hi", label: "Hindi" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
];

export default function Dashboard() {
  const { user, loading, setUser } = useAuth({ redirectIfUnauthed: false });
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [billing, setBilling] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [presets, setPresets] = useState([]);
  const [activePreset, setActivePreset] = useState(null);  // slug of currently-applied preset
  const [form, setForm] = useState({
    name: "",
    persona: "You are a friendly WhatsApp concierge. Keep replies under 2 sentences and warm.",
    voice: "alloy",
    language: "auto",
    knowledge: "",
    style_guide: "",
    intake_form: [],
  });

  useEffect(() => {
    if (loading) return;
    if (!user) return; // show login CTA
    (async () => {
      try {
        const [a, b, u, p] = await Promise.all([
          api.get("/agents"),
          api.get("/billing/me"),
          api.get("/billing/usage").catch(() => ({ data: null })),
          api.get("/presets").catch(() => ({ data: { presets: [] } })),
        ]);
        let initialAgents = a.data || [];
        setBilling(b.data);
        setUsage(u.data);
        setPresets(p.data?.presets || []);

        // If they came from a landing-page preset demo, auto-spin up that agent
        // and drop them into it. Server is idempotent — safe to call every mount.
        if (initialAgents.length === 0) {
          try {
            const applied = await api.post("/agents/apply-signup-preset", {});
            if (applied.data?.applied && applied.data?.agent) {
              toast.success(`Your ${applied.data.agent.name} agent is ready — let's customize it.`);
              navigate(`/dashboard/agents/${applied.data.agent.id}`);
              return;
            }
          } catch { /* never blocks the dashboard from loading */ }
        }
        setAgents(initialAgents);
      } catch {
        toast.error("Could not load agents");
      } finally {
        setFetching(false);
      }
    })();
  }, [loading, user, navigate]);

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/agents", form);
      setAgents((a) => [res.data, ...a]);
      toast.success("Agent created");
      setShowCreate(false);
      navigate(`/dashboard/agents/${res.data.id}`);
    } catch {
      toast.error("Failed to create agent");
    } finally {
      setCreating(false);
    }
  };

  const applyPreset = async (slug) => {
    try {
      const r = await api.get(`/presets/${slug}`);
      const p = r.data;
      setForm((f) => ({
        ...f,
        name: f.name || p.suggested_name || p.label,
        persona: p.persona,
        voice: p.voice,
        language: p.language,
        style_guide: p.style_guide || "",
        intake_form: p.intake_form || [],
      }));
      setActivePreset(slug);
      toast.success(`Preset "${p.label}" applied`);
    } catch {
      toast.error("Couldn't load preset");
    }
  };

  const startBlankAgent = () => {
    setActivePreset(null);
    setForm({
      name: "",
      persona: "You are a friendly WhatsApp concierge. Keep replies under 2 sentences and warm.",
      voice: "alloy",
      language: "auto",
      knowledge: "",
      style_guide: "",
      intake_form: [],
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    try {
      await api.delete(`/agents/${id}`);
      setAgents((a) => a.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F]">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
        <Navbar user={null} />
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="font-serif text-5xl mb-6">Sign in to your dashboard</h1>
          <p className="font-mono text-sm text-[#A39A8F] mb-10">Email, password, Google — your call.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/login")}
              data-testid="goto-login"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-4 btn-press"
            >
              Sign in →
            </button>
            <button
              onClick={() => navigate("/signup")}
              data-testid="goto-signup"
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-8 py-4 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
            >
              Create account
            </button>
          </div>
          <button
            onClick={startGoogleLogin}
            data-testid="login-google"
            className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB]"
          >
            or continue with Google →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        {billing?.founding_member && <FoundingMemberCallout user={user} />}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-3 flex items-center gap-4 flex-wrap">
              <span>Hello, {user.name?.split(" ")[0] || "builder"}</span>
              {billing?.founding_member && (
                <span
                  data-testid="founding-member-badge"
                  className="inline-flex items-center gap-2 px-3 py-1 border border-[#D95B38] bg-[#D95B38]/10 text-[#D95B38]"
                  title="Founding pricing locked in for life"
                >
                  <Star className="w-3 h-3 fill-current" strokeWidth={2} />
                  Founding member
                </span>
              )}
              {billing && (
                <span
                  data-testid="billing-badge"
                  className={`inline-flex items-center gap-2 px-3 py-1 border ${billing.active ? "border-[#D95B38] text-[#D95B38]" : "border-[#F5F2EB]/20 text-[#A39A8F]"}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${billing.active ? "bg-[#D95B38]" : "bg-[#A39A8F]"}`} />
                  {billing.trial && billing.active
                    ? `Free Trial · ${billing.days_remaining}d left`
                    : billing.active
                      ? (billing.lifetime ? "Pro · Lifetime" : `Pro · ${billing.plan}`)
                      : "Trial expired"}
                </span>
              )}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Your <span className="italic text-[#D95B38]">voice agents</span></h1>
          </div>
          <div className="flex gap-3 self-start">
            {user?.is_admin && (
              <>
                <a
                  href="/dashboard/admin/health"
                  data-testid="admin-health-link"
                  className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38] hover:text-[#D95B38] px-6 py-3 transition-colors"
                >
                  Ops →
                </a>
                <a
                  href="/dashboard/admin/voices"
                  data-testid="admin-voices-link"
                  className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38] hover:text-[#D95B38] px-6 py-3 transition-colors"
                >
                  Voices →
                </a>
                <a
                  href="/dashboard/admin/blog"
                  data-testid="admin-blog-link"
                  className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38] hover:text-[#D95B38] px-6 py-3 transition-colors"
                >
                  Blog admin →
                </a>
              </>
            )}
            {billing && !billing.active && (
              <a
                href="/#pricing"
                data-testid="upgrade-cta"
                className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 hover:opacity-90 transition-opacity"
              >
                Activate plan →
              </a>
            )}
            {billing && billing.active && billing.trial && billing.days_remaining !== null && billing.days_remaining <= 3 && (
              <a
                href="/#pricing"
                data-testid="upgrade-cta"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-6 py-3 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors"
              >
                Upgrade ({billing.days_remaining}d left)
              </a>
            )}
            {billing && billing.active && !billing.trial && !billing.lifetime && (
              <a
                href="/#pricing"
                data-testid="upgrade-cta"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-6 py-3 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors"
              >
                Manage plan
              </a>
            )}
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-agent-button"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press flex items-center gap-3"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New agent
            </button>
          </div>
        </div>

        {billing && !billing.active && (
          <div
            data-testid="trial-expired-banner"
            className="mb-10 border border-[#D95B38] bg-[#D95B38]/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2">Trial ended</div>
              <div className="font-serif text-2xl mb-1">Your agents are paused.</div>
              <div className="font-mono text-sm text-[#A39A8F] max-w-xl">
                Visitors get a "this agent is paused" message until you activate a plan. Your data, contacts, and settings are all safe — pick up exactly where you left off.
              </div>
            </div>
            <a
              href="/#pricing"
              data-testid="trial-banner-cta"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 hover:opacity-90 transition-opacity self-start whitespace-nowrap"
            >
              See plans →
            </a>
          </div>
        )}

        {billing && billing.active && billing.trial && billing.days_remaining !== null && billing.days_remaining <= 3 && (
          <div
            data-testid="trial-ending-banner"
            className="mb-10 border border-[#F5F2EB]/20 bg-[#1A1715] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div className="font-mono text-sm text-[#E0D8CA]">
              <span className="text-[#D95B38] font-medium">Heads up:</span> your free trial ends in <span className="text-[#F5F2EB] font-medium">{billing.days_remaining} day{billing.days_remaining === 1 ? "" : "s"}</span>. Activate a plan to keep your agents online.
            </div>
            <a
              href="/#pricing"
              data-testid="trial-ending-banner-cta"
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-5 py-2.5 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors self-start whitespace-nowrap"
            >
              Upgrade →
            </a>
          </div>
        )}

        {billing?.payment_failed && (
          <div
            data-testid="payment-failed-banner"
            className="mb-10 border border-[#D95B38]/60 bg-[#2B1815] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div className="font-mono text-sm text-[#F5F2EB] leading-relaxed">
              <span className="text-[#D95B38] font-medium">Payment failed.</span> We couldn't charge your card for your last AgentReply renewal. Your agents are still online for now — please update your payment method to avoid interruption.
            </div>
            <a
              href="/#pricing"
              data-testid="payment-failed-banner-cta"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-5 py-2.5 hover:opacity-90 transition-opacity self-start whitespace-nowrap btn-press"
            >
              Update payment →
            </a>
          </div>
        )}

        {usage && usage.near_limit && !usage.over_limit && (
          <div
            data-testid="usage-warning-banner"
            className="mb-10 border border-[#F5F2EB]/20 bg-[#1A1715] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div className="font-mono text-sm text-[#E0D8CA] leading-relaxed">
              <span className="text-[#D95B38] font-medium">{usage.percent}% used.</span> {usage.used.toLocaleString()} of {usage.limit.toLocaleString()} messages this month. Upgrade to avoid pausing when you hit the cap.
            </div>
            <a href="/#pricing" data-testid="usage-warning-banner-cta"
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-5 py-2.5 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors self-start whitespace-nowrap">
              View plans →
            </a>
          </div>
        )}

        {usage?.over_limit && (
          <div
            data-testid="usage-cap-banner"
            className="mb-10 border border-[#D95B38]/60 bg-[#2B1815] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div className="font-mono text-sm text-[#F5F2EB] leading-relaxed">
              <span className="text-[#D95B38] font-medium">Message cap reached.</span> Your agents have paused replying ({usage.used.toLocaleString()} / {usage.limit.toLocaleString()} this month). Upgrade or wait until next month — your data is safe either way.
            </div>
            <a href="/#pricing" data-testid="usage-cap-banner-cta"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-5 py-2.5 hover:opacity-90 transition-opacity self-start whitespace-nowrap btn-press">
              Upgrade now →
            </a>
          </div>
        )}
        {fetching ? (
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F]">Loading agents…</div>
        ) : agents.length === 0 ? (
          <div className="border border-dashed border-[#F5F2EB]/15 p-16 text-center" data-testid="empty-state">
            <div className="font-serif text-3xl mb-3">No agents yet.</div>
            <div className="font-mono text-sm text-[#A39A8F] mb-8">Build your first one in under five minutes.</div>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="empty-create-agent"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press"
            >
              Create your first agent
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agents-grid">
            {agents.map((a) => (
              <div
                key={a.id}
                data-testid={`agent-card-${a.id}`}
                onClick={() => navigate(`/dashboard/agents/${a.id}`)}
                className="group cursor-pointer bg-[#1A1715] border border-[#F5F2EB]/10 p-8 hover:border-[#D95B38]/50 hover:bg-[#221C19] transition-all"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="w-10 h-10 bg-[#D95B38]/15 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-[#D95B38]" strokeWidth={1.5} />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#A39A8F] group-hover:text-[#D95B38] transition-colors" strokeWidth={1.5} />
                </div>
                <div className="font-serif text-2xl mb-1">{a.name}</div>
                <div className="font-mono text-xs text-[#A39A8F] mb-6 line-clamp-2">{a.persona}</div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
                  <div className="flex gap-3 items-center">
                    <span className="flex items-center gap-1.5"><Mic className="w-3 h-3" strokeWidth={1.5} />{a.voice}</span>
                    <span>·</span>
                    <span>{a.language}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }}
                    className="text-[#A39A8F] hover:text-[#D95B38] transition-colors"
                    data-testid={`delete-agent-${a.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-[#F5F2EB]/10 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${a.whatsapp_connected ? "bg-green-500 pulse-green" : "bg-[#A39A8F]"}`} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
                    {a.whatsapp_connected ? "WhatsApp connected" : "Not connected"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6 backdrop-blur" data-testid="create-agent-modal">
          <div className="bg-[#1A1715] border border-[#F5F2EB]/15 w-full max-w-2xl p-8 md:p-12 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-8">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-2">New agent</div>
                <h2 className="font-serif text-3xl">Tell us about <span className="italic">them</span>.</h2>
              </div>
              <button onClick={() => setShowCreate(false)} className="font-mono text-2xl text-[#A39A8F] hover:text-[#F5F2EB]" data-testid="close-create-modal">×</button>
            </div>

            {/* Preset picker — speeds up onboarding for common use cases */}
            {presets.length > 0 && (
              <div className="mb-8 border border-[#F5F2EB]/10 bg-[#0F0D0C] p-5" data-testid="preset-picker">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38]">Start from a preset</div>
                    <div className="font-serif text-lg mt-1">Pre-built persona + voice + intake form</div>
                  </div>
                  {activePreset && (
                    <button
                      type="button"
                      onClick={startBlankAgent}
                      data-testid="preset-clear"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] transition-colors"
                    >
                      Clear preset ×
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {presets.map((p) => {
                    const selected = activePreset === p.slug;
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        onClick={() => applyPreset(p.slug)}
                        data-testid={`preset-${p.slug}`}
                        className={`text-left p-3 border transition-all ${
                          selected
                            ? "border-[#D95B38] bg-[#221C19]"
                            : "border-[#F5F2EB]/10 hover:border-[#D95B38]/60 hover:bg-[#1A1715]"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-xl shrink-0">{p.icon}</div>
                          <div className="min-w-0 flex-1">
                            <div className="font-serif text-base text-[#F5F2EB]">{p.label}</div>
                            <div className="font-mono text-[10px] text-[#A39A8F] mt-0.5 leading-snug">{p.tagline}</div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39A8F]/60 mt-1.5">
                              {p.intake_field_count} intake field{p.intake_field_count === 1 ? "" : "s"} · {p.best_for.slice(0, 2).join(" / ")}
                            </div>
                          </div>
                          {selected && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38]">✓</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-6">
              <Field label="Agent name">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Sasha, the concierge"
                  data-testid="form-agent-name"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-lg focus:outline-none focus:border-[#D95B38] placeholder:text-[#A39A8F]/40"
                />
              </Field>

              <Field label="Persona / system prompt">
                <textarea
                  required
                  rows={4}
                  value={form.persona}
                  onChange={(e) => setForm({ ...form, persona: e.target.value })}
                  data-testid="form-agent-persona"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
                />
              </Field>

              <div className="grid grid-cols-2 gap-6">
                <Field label="Voice">
                  <select
                    value={form.voice}
                    onChange={(e) => setForm({ ...form, voice: e.target.value })}
                    data-testid="form-agent-voice"
                    className="w-full bg-[#1A1715] border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
                  >
                    {VOICES.map((v) => <option key={v} value={v} className="bg-[#1A1715]">{v}</option>)}
                  </select>
                </Field>
                <Field label="Language">
                  <select
                    value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                    data-testid="form-agent-language"
                    className="w-full bg-[#1A1715] border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
                  >
                    {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-[#1A1715]">{l.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Knowledge (optional)">
                <textarea
                  rows={3}
                  placeholder="Hours, menu, FAQ snippets, anything the agent should know…"
                  value={form.knowledge}
                  onChange={(e) => setForm({ ...form, knowledge: e.target.value })}
                  data-testid="form-agent-knowledge"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none placeholder:text-[#A39A8F]/40"
                />
              </Field>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={creating}
                  data-testid="form-submit"
                  className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-3 btn-press flex items-center gap-3 disabled:opacity-60"
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create agent
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB]"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-1">{label}</span>
      {children}
    </label>
  );
}

function FoundingMemberCallout({ user }) {
  const [copied, setCopied] = useState(false);
  // Build the referral link — `?ref=widget&u={user_id}` matches the existing iter-37
  // referral-attribution scheme. Frontend Landing reads these and stamps localStorage,
  // then AuthPage forwards them on signup. So the friend's signup auto-attributes to
  // the founding member, and at checkout time billing.py looks up `referred_by` and
  // grants founding pricing even after the public 100-slot quota closes.
  const base = (typeof window !== "undefined" && window.location.origin) || "https://agentreply.io";
  const link = `${base}/?ref=widget&u=${encodeURIComponent(user.user_id)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="founding-callout"
      className="mb-10 border border-[#D95B38]/50 bg-gradient-to-br from-[#221C19] via-[#1A1715] to-[#0F0D0C] p-6 md:p-8"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
        <div className="flex items-center gap-4 shrink-0">
          <div className="w-12 h-12 rounded-full bg-[#D95B38]/15 border border-[#D95B38] flex items-center justify-center">
            <Star className="w-5 h-5 fill-[#D95B38] text-[#D95B38]" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38]">Founding member</div>
            <div className="font-serif text-xl text-[#F5F2EB] mt-0.5">Your rate is locked in for life</div>
          </div>
        </div>
        <div className="flex-1 min-w-0 md:border-l md:border-[#F5F2EB]/10 md:pl-8">
          <div className="font-mono text-xs text-[#A39A8F] leading-relaxed mb-3">
            <span className="text-[#F5F2EB]">Share founding pricing with friends.</span> Anyone who signs up with your link gets the same founding rate — even after the public quota closes.
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={link}
              data-testid="founding-referral-link"
              onClick={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-[#0F0D0C] border border-[#F5F2EB]/15 font-mono text-xs text-[#A39A8F] px-3 py-2.5 focus:outline-none focus:border-[#D95B38] focus:text-[#F5F2EB]"
            />
            <button
              onClick={copy}
              data-testid="founding-referral-copy"
              className="px-4 py-2.5 bg-[#D95B38] text-[#F5F2EB] font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#D95B38]/90 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {copied ? <><Check className="w-3 h-3" strokeWidth={2.5} /> Copied</> : <><Copy className="w-3 h-3" strokeWidth={1.8} /> Copy link</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
